// src/components/AccessModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Accordion } from "react-bootstrap";
import {
  Menu,
  Website,
  WebsiteAccess,
  User,
} from "../../PropTypes/UserManagementTypes";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

type Props = {
  show: boolean;
  onHide: () => void;
  websiteList: Website[];
  allPermission: Menu[];
  editingUser?: User | null;
  initialAccess?: WebsiteAccess[]; // optional pre-filled access
  onSaveAccess: (
    userId: number,
    selectedWebsitesAccess: WebsiteAccess[]
  ) => Promise<any>;
  fetchUserPermissions?: (userId: number) => Promise<any>;
  disabledButton?: boolean;
  allManageAccessWebsites: string[];
};

const AccessModal: React.FC<Props> = ({
  show,
  onHide,
  websiteList,
  allPermission,
  editingUser,
  onSaveAccess,
  initialAccess = [],
  fetchUserPermissions,
  disabledButton,
  allManageAccessWebsites,
}) => {
  const { t } = useTranslation();
  const [selectedWebsitesAccess, setSelectedWebsitesAccess] =
    useState<WebsiteAccess[]>(initialAccess);
  const [websiteSearch, setWebsiteSearch] = useState("");
  const { user } = useSelector((store: StoreProptypes) => store);
  const matchedWebsites = websiteList.filter((item) =>
    allManageAccessWebsites.includes(item.domain)
  );
  const websiteData = user?.department === 1 ? matchedWebsites : websiteList;
  useEffect(() => {
    if (show) {
      (async () => {
        if (editingUser && fetchUserPermissions) {
          try {
            const resp = await fetchUserPermissions(editingUser.id);
            if (resp && resp.length > 0) {
              const defaultAccessEdit: WebsiteAccess[] = (
                editingUser.website || []
              ).map((domain: string) => {
                const matched = resp.find(
                  (p: any) => p.website_domain === domain
                );
                return {
                  website: domain,
                  sidebarMenus:
                    matched?.permissions?.map((perm: any) => perm.name) || [],
                };
              });
              setSelectedWebsitesAccess(defaultAccessEdit);
              return;
            }
          } catch (err) {
            // ignore, fall back to initialAccess/default
            console.error("fetchUserPermissions error", err);
          }
        }
        // fallback: default all websites with all permissions
        const defaultAccess = websiteData.map((site) => ({
          website: site.domain,
          sidebarMenus: allPermission.map((m) => m.name),
        }));
        setSelectedWebsitesAccess(defaultAccess);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingUser, websiteData, allPermission]);

  const filteredWebsiteList = useMemo(
    () =>
      websiteData.filter((s) =>
        s.domain.toLowerCase().includes(websiteSearch.toLowerCase())
      ),
    [websiteData, websiteSearch]
  );

  const handleSave = async () => {
    if (!editingUser) return;
    if (selectedWebsitesAccess.length === 0) {
      // show toast via parent (we assume the parent/hook will handle)
      return;
    }
    await onSaveAccess(editingUser.id, selectedWebsitesAccess);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("Website Access & Permissions")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {editingUser && (
          <p className="mb-3">
            {t("Assign access for:")} <strong>{editingUser.name}</strong>
          </p>
        )}
        <div className="p-2 mb-1 mt-1">
          <Form.Control
            type="text"
            placeholder={t("Search Website...")}
            value={websiteSearch}
            onChange={(e) => setWebsiteSearch(e.target.value)}
          />
        </div>
        <div style={{ maxHeight: "330px", overflowY: "auto", padding: "8px" }}>
          {filteredWebsiteList.length === 0 ? (
            <div>{t("No Website found")}</div>
          ) : (
            <Accordion alwaysOpen>
              {filteredWebsiteList.map((site, idx) => {
                const siteAccess = selectedWebsitesAccess.find(
                  (w) => w.website === site.domain
                ) || { website: site.domain, sidebarMenus: [] };
                const isWebsiteSelected = selectedWebsitesAccess.some(
                  (w) => w.website === site.domain
                );

                return (
                  <Accordion.Item eventKey={String(idx)} key={site.id}>
                    <Accordion.Header>
                      <Form.Check
                        type="checkbox"
                        label={site.domain}
                        className="checkbox-sm"
                        disabled={false} 
                        style={{ pointerEvents: disabledButton ? "none" : "auto", opacity: disabledButton ? 0.8 : 1, }}  
                        checked={isWebsiteSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedWebsitesAccess((prev) => [
                              ...prev,
                              {
                                website: site.domain,
                                sidebarMenus: allPermission.map((m) => m.name),
                              },
                            ]);
                          } else {
                            setSelectedWebsitesAccess((prev) =>
                              prev.filter((w) => w.website !== site.domain)
                            );
                          }
                        }}
                      />
                    </Accordion.Header>
                    <Accordion.Body>
                      <hr className="my-2" />
                      {allPermission.map((menu) => {
                        const isChecked = siteAccess.sidebarMenus.includes(
                          menu.name
                        );
                        return (
                          <div
                            key={menu.id}
                            className="d-flex align-items-center mb-1"
                            style={{ gap: "6px" }}
                          >
                            <input
                              type="checkbox"
                              id={`menu-${site.domain}-${menu.id}`}
                              className="form-check-input checkbox-sm"
                              checked={isChecked}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                pointerEvents: disabledButton ? "none" : "auto",
                                opacity: disabledButton ? 0.5 : 1,
                                fontSize: 11,
                              }}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedWebsitesAccess((prev) => {
                                  const exists = prev.find(
                                    (w) => w.website === site.domain
                                  );
                                  if (exists) {
                                    const updated = prev
                                      .map((w) =>
                                        w.website === site.domain
                                          ? {
                                              ...w,
                                              sidebarMenus: checked
                                                ? [...w.sidebarMenus, menu.name]
                                                : w.sidebarMenus.filter(
                                                    (m) => m !== menu.name
                                                  ),
                                            }
                                          : w
                                      )
                                      .filter((w) => w.sidebarMenus.length > 0);
                                    return updated;
                                  } else if (checked) {
                                    return [
                                      ...prev,
                                      {
                                        website: site.domain,
                                        sidebarMenus: [menu.name],
                                      },
                                    ];
                                  }
                                  return prev;
                                });
                              }}
                            />
                            <label
                              htmlFor={`menu-${site.domain}-${menu.id}`}
                              className="form-check-label"
                              onClick={(e) => e.stopPropagation()}
                              style={{ cursor: "pointer" }}
                            >
                              {menu.title}
                            </label>
                          </div>
                        );
                      })}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={onHide}>
          {t("Cancel")}
        </Button>
        <Button
          variant="success"
          disabled={disabledButton}
          onClick={handleSave}
        >
          {t("Save Access")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccessModal;
